import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from 'src/app/_services/auth/auth.service';
import { BlService } from 'src/app/_services/bl/bl.service';
import { LocalStorageService } from 'src/app/_services/localStorage/local-storage.service';
import { CabotageBoat, OperationType, TableConfig } from 'src/app/interfaces/bl.interfaces';

@Component({
  selector: 'app-bl-lista',
  templateUrl: './bl-lista.component.html',
  styleUrls: ['./bl-lista.component.css']
})
/**
 * Página de listado de barcos.
 *
 * Muestra los barcos según el año y el tipo de operación seleccionados
 * (general, importación, exportación, cabotaje), valida los permisos del
 * perfil, saluda al usuario y permite filtrar la lista por tipo y estado.
 */
export class BlListaComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  textFilter: string = '';
  table_config: TableConfig = {
    tableHeads: [
      ['No. barco', 'operation_number'],
      ['Nombre del barco', 'boats_name'],
      ['Toneladas totales', 'bl_tonnage'],
      ['Tipo de operación', 'operation_type'],
      ['Fecha atraque', 'start_operations'],
      ['Fecha desatraque', 'end_operations'],
      ['Toneladas de salida', 'output_tonnage'],
      ['Toneladas restantes', 'remaining_tonnage'],
      ['Estatus de abandono', 'abandonment_status']
    ],
    tableHeadsContainer: ['Detalle'],
    styles: {
      table_heads: {
        'grid-template-columns': 'minmax(60px, auto) repeat(8, minmax(130px, 1fr)) minmax(60px, auto)',
        'min-width': '1160px',
      },
      table_row: {
        'grid-template-columns': 'minmax(60px, auto) repeat(8, minmax(130px, 1fr)) minmax(60px, auto)',
        'min-width': '1160px',
      },
      container_icons: {
        'grid-template-columns': 'repeat(1, 1fr)',
      }
    },
    arrayFields: [
      'operation_number',
      'boats_name',
      'bl_tonnage',
      'operation_type',
      'start_operations',
      'end_operations',
      'output_tonnage',
      'remaining_tonnage',
      'abandonment_status'
    ],
    iconos: [["/ship-list", "operation_number", 'bi bi-eye']],
    mensaje: "Barcos no encontrados.",
    filter: true,
    totals: {
      show: false,
      content: [],
      total: 0,
    }
  };
  contadorRender: number = 1;
  saludo: string = '';
  titulo: string = 'Listado de Barcos General';
  overlayError: boolean = false;
  general: boolean = true;
  importacion: boolean = false;
  exportacion: boolean = false;
  cabotajeImp: boolean = false;
  cabotajeExp: boolean = false;
  url: string = '';
  year: FormControl;
  type: FormControl<number | null> = new FormControl(0);
  yearsList: string[] = ['2019', String(new Date().getFullYear())];
  shipType: FormControl = new FormControl('Todos');
  authorizations: any;

  // Overlay
  overlay: boolean = false;
  message: string = '';
  icon: string = '';

  get blsArray() {
    if (this.contadorRender <= 3) {
      this.contadorRender++;
      return this.sortArray(this.blServices.blsFilter, 'operation_number');
    }
    return this.blServices.blsFilter
  }

  get blLoader() {
    return this.blServices.loader;
  }

  get shipError() {
    return this.blServices.ships;
  }

  get overlayErrorShips() {
    return this.blServices.overlayError;
  }

  constructor(
    private blServices: BlService,
    private localStorageServices: LocalStorageService,
    private authService: AuthService,
    private router: Router
  ) {
    const currentDate = new Date();
    const hora = currentDate.getHours();
    if ( hora < 12 ) {
      this.saludo = 'Buenos días!';
    } else if( hora >= 12 && hora < 18) {
      this.saludo = 'Buenas tardes!';
    } else {
      this.saludo = 'Buenas noches!';
    }
    this.year = new FormControl(new Date().getFullYear());
  }

  ngOnInit(): void {
    this.blServices.path = '/ship-list';
    this.fillYearsList();
    this.blServices.loader = true;
    // Validar si el usuario tiene permisos para acceder a la lista de barcos
    this.authorizations = this.localStorageServices.getData('authorizations');
    if (this.authorizations.menu_options && this.authorizations.menu_options.length) {
      const boat_list = this.authorizations.menu_options.find( (menu: any) => menu.menu_id === 1);
      if (!boat_list) {
        this.router.navigate(['/'+this.authorizations.menu_options[0].router_link]);
      }
    } else {
      this.message = 'Este perfil no tiene permisos para acceder al sistema de inventarios';
      this.icon = 'bi-exclamation-circle-fill';
      this.overlay = true;
      setTimeout(() => {
        this.overlay = false;
        this.authService.logout();
      }, 3000);
    }

    // Obtener el nombre del usuario y mostrarlo en el saludo
    const email = this.localStorageServices.getData('userEmail');
    const user = email.split('@')[0].toUpperCase();
    this.saludo = `${this.saludo} ${user}`;

    // Por defecto el tipo de operación es General
    this.blServices.typeOperation = OperationType.General;
    // Llamar al servicio para obtener la lista de barcos
    this.blServices.getBlTable();

    // Escuchar el tipo de barco(activo o no activo) y filtrar la lista de barcos
    this.shipType.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: string) => {
        this.blServices.shipType = value;
        this.blServices.sortByFilter();
      });
  }

  ngOnDestroy(): void {
    this.blServices.cancelRequests();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Selecciona el tipo de operación a mostrar, actualiza el título y aplica
   * el filtro correspondiente.
   * @param type - Tipo de operación (general, importación, exportación, cabotaje).
   */
  typeBoat(type: number) {
    if (this.blLoader) {
      return;
    }
    this.blServices.typeOperation = type;
    this.type.setValue(type);
    this.general = false;
    this.importacion = false;
    this.exportacion = false;
    this.cabotajeImp = false;
    this.cabotajeExp = false;
    switch (type) {
      case OperationType.General:
        this.general = true;
        this.titulo = 'Listado de Barcos General';
        this.blServices.sortByFilter();
        break;
      case OperationType.Importation:
        this.importacion = true;
        this.titulo = 'Listado de Barcos de Importación';
        this.blServices.sortByFilter();
        break;
      case OperationType.Exportation:
        this.exportacion = true;
        this.titulo = 'Listado de Exportación';
        this.blServices.sortByFilter();
        break;
      case OperationType.CabotageImp:
        this.cabotajeImp = true;
        this.titulo = 'Listado de Cabotaje de Importación';
        this.blServices.sortByFilter();
        break;
      case OperationType.CabotageExp:
        this.cabotajeExp = true;
        this.titulo = 'Listado de Cabotaje de Exportación';
        this.blServices.sortByFilter();
        break;
      default:
        this.general = true;
        this.titulo = 'Listado de Barcos General';
        this.blServices.sortByFilter();
        break;
    }
  }

  /**
   * Ordena descendentemente un arreglo por la clave indicada.
   * @param array - Arreglo a ordenar.
   * @param key - Clave del objeto por la que se ordena.
   * @returns El arreglo ordenado.
   */
  sortArray<T>(array: T[], key: keyof T): T[] {
    return array.sort(function (a, b) {
      if (b[key] < a[key]) {
        return -1;
      }
      if (b[key] > a[key]) {
        return 1;
      }
      return 0;
    });
  }

  changeYear() {
    this.blServices.getBlTable(Number(this.year.value));
  }

  changeType() {
    const currentType = this.type.value;
    this.typeBoat(currentType ? currentType : OperationType.General);
  }

  fillYearsList() {
    const firstYear: number = Number(this.yearsList[0]);
    const lastYear: number = Number(this.yearsList[1]);
    for (let index = firstYear; index < lastYear; index++) {
      if (!this.yearsList.includes(String(index))) {
        this.yearsList.push(String(index));
      }
    }
    this.yearsList.sort((a, b) => Number(b) - Number(a));
  }
}
